// The Advanced references' analysis (#234 part 2a, G8 PR 3a), as live's `useReferenceSetup` and
// `useInspirationExtractionStatus`: each reference's structure is read, and one with none, or a
// failed one, is analysed once; its job's events say when the structure is ready. A creative a
// brand import brought in with live's specification follows that one and is not analysed, as
// live's Clone carries a file's `inspirationImageStructure` (issue 828, item 23); the start reads
// it too.
import { useQueries, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { ApiError } from "../../../api/errors";
import {
  getAssetAdStructure,
  getGetAssetAdStructureQueryKey,
  getGetJobQueryKey,
  getGetJobQueryOptions,
  getImportedGeneration,
  startJob,
} from "../../../api/generated/endpoints/generation/generation";
import type { AdCreativeStructure, AdStructure, Job } from "../../../api/generated/model";
import type { StreamStatus } from "../../../api/events";
import { attemptKey } from "../../../platform/idempotency";
import { canChange, jobRefetchInterval } from "../../../platform/jobs/refetch";
import { useStreamStatus } from "../../../platform/stream-status";

/** A reference while its analysis runs, or once it is done: its structure, or none if it failed. */
export type Analysis =
  { state: "analysing" } | { state: "ready"; structure: AdCreativeStructure | null };

/** Live's tooltip on an image while it is analysed (`settings-preview/image-item`). */
export const ANALYSING = "Analysing...";

/** Live's words when an analysis cannot start (`useInspirationStructureCheck`). */
export const ANALYSIS_REFUSED = "Failed to extract inspiration structure";

/** How soon a structure is read again once its job ended: its fill commits just after the job. */
const SETTLE_MS = 1_000;
const FALLBACK_MS = 15_000;

/**
 * A reference's read: its structure (null before any analysis), live's specification a brand
 * import kept of it (null for none), and whether a start was refused.
 */
interface Read {
  structure: AdStructure | null;
  imported: AdCreativeStructure | null;
  refused: boolean;
}

async function readStructure(
  teamId: string,
  brandId: string,
  assetId: string,
  signal: AbortSignal,
): Promise<AdStructure | null> {
  try {
    return await getAssetAdStructure(teamId, brandId, assetId, { signal });
  } catch (error) {
    // No analysis yet is a 404: none, not an error.
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

async function readImported(
  teamId: string,
  brandId: string,
  assetId: string,
  signal: AbortSignal,
): Promise<AdCreativeStructure | null> {
  try {
    return (await getImportedGeneration(teamId, brandId, assetId, { signal })).ad_specification;
  } catch (error) {
    // A file no import brought in, or brought in with no details, is a 404: none.
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/** When a structure under way is read again: soon once its job ended, else by the stream. */
function settleInterval(
  queryClient: QueryClient,
  teamId: string,
  read: Read | undefined,
  stream: StreamStatus | undefined,
): number | false {
  const structure = read?.structure;
  if (structure?.status !== "in_progress") return false;
  const job = queryClient.getQueryData<Job>(getGetJobQueryKey(teamId, structure.job_id));
  if (job !== undefined && !canChange(job.status)) return SETTLE_MS;
  return stream === "live" ? false : FALLBACK_MS;
}

function analysisOf(read: Read | undefined): Analysis {
  const structure = read?.structure;
  if (read === undefined || structure?.status === "in_progress") return { state: "analysing" };
  if (structure?.status === "completed") return { state: "ready", structure: structure.structure };
  // None, failed or refused: live's imported specification where there is one, else ready with no
  // structure, silently, as live's.
  return { state: "ready", structure: read.imported };
}

/**
 * The analysis of each reference of an Advanced draft, by asset id. Each read is the structure's
 * `GET`; when it says none (404) or `failed`, the read takes the file's imported specification
 * where a brand import kept one, else starts `image.analysis` for the reference, once while this
 * composer lives, and reads again (the start drafts the row `in_progress`). A start for a source
 * analysed already answers with that analysis (G8), so it costs nothing.
 */
export function useAdStructures(teamId: string, brandId: string, assetIds: readonly string[]) {
  const queryClient = useQueryClient();
  const stream = useStreamStatus();
  const asked = useRef(new Set<string>());
  const refused = useRef(new Set<string>());
  const reads = useQueries({
    queries: assetIds.map((assetId) => ({
      // Apart from the plain structure read: this one may start an analysis.
      queryKey: [...getGetAssetAdStructureQueryKey(teamId, brandId, assetId), "analysis"],
      queryFn: async ({ signal }: { signal: AbortSignal }): Promise<Read> => {
        const structure = await readStructure(teamId, brandId, assetId, signal);
        const due = structure === null || structure.status === "failed";
        const imported = due ? await readImported(teamId, brandId, assetId, signal) : null;
        if (!due || imported !== null || asked.current.has(assetId)) {
          return { structure, imported, refused: refused.current.has(assetId) };
        }
        asked.current.add(assetId);
        const body = {
          type: "image.analysis" as const,
          source: { kind: "asset" as const, asset_id: assetId },
        };
        try {
          await startJob(teamId, brandId, body, { "Idempotency-Key": attemptKey(body, undefined) });
        } catch {
          refused.current.add(assetId);
          return { structure, imported, refused: true };
        }
        const read = await readStructure(teamId, brandId, assetId, signal);
        return { structure: read, imported, refused: false };
      },
      retry: false,
      refetchInterval: (query: { state: { data: Read | undefined } }) =>
        settleInterval(queryClient, teamId, query.state.data, stream),
    })),
  });
  // The jobs of the analyses under way, read as a job's page reads one (stream events, then the
  // fallback timer while the stream is down); their ends make the reads above settle.
  const running = [
    ...new Set(
      reads.flatMap((read) =>
        read.data?.structure?.status === "in_progress" ? [read.data.structure.job_id] : [],
      ),
    ),
  ];
  useQueries({
    queries: running.map((jobId) => ({
      ...getGetJobQueryOptions(teamId, jobId),
      refetchInterval: (query: { state: { data: Job | undefined } }) =>
        jobRefetchInterval(query.state.data?.status, stream),
    })),
  });
  const analyses = new Map(
    assetIds.map((assetId, at) => {
      const read = reads[at];
      // A read the API refused leaves the reference ready with no structure.
      const analysis: Analysis =
        read?.isError === true ? { state: "ready", structure: null } : analysisOf(read?.data);
      return [assetId, analysis] as const;
    }),
  );
  return {
    /** A reference's analysis; analysing until its read answers. */
    of: (assetId: string): Analysis => analyses.get(assetId) ?? { state: "analysing" },
    /** Some reference is still analysed: the chip and the start wait. */
    analysing: [...analyses.values()].some((analysis) => analysis.state === "analysing"),
    /** An analysis the API refused to start: live's words show under the references. */
    refused: reads.some((read) => read.data?.refused === true),
  };
}

export type AdStructures = ReturnType<typeof useAdStructures>;
